import React, { useState, useEffect } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import _ from "lodash";
import { signInWithEmailAndPassword, sendSignInLinkToEmail } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { ArrowRight } from "lucide-react";
import { auth, db } from "../../firebase.js";
import AuthLayout from "../../components/AuthLayout.js";
import Message from "../../components/Message.js";
import Button from "../../components/Button.js";

// Bepaalt de juiste bestemming na het inloggen op basis van het accounttype.
// Dienstverleners horen op /mijn-dienst (beheer van hun vermelding),
// feestlocaties in het reguliere dashboard. Zo hoeft de gebruiker zichzelf niet
// te classificeren bij het inloggen en belandt niemand op het verkeerde overzicht.
async function resolveHomePath(uid) {
  try {
    const snap = await getDocs(query(collection(db, "services"), where("uid", "==", uid)));
    if (!snap.empty) return "/mijn-dienst";
  } catch (err) {
    console.log(err);
  }
  return "/";
}

// Geef de oorspronkelijke bestemming door aan de magic-link, zodat /action na
// het inloggen terugstuurt naar bv. de aanvraag in plaats van het dashboard.
// Alleen interne paden; /action valideert `next` nogmaals.
function nextParam(from) {
  if (typeof from !== "string" || !from.startsWith("/") || from.startsWith("//")) return "";
  return "&next=" + encodeURIComponent(from);
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const [magicEmail, setMagicEmail] = useState("");
  const [magicStatus, setMagicStatus] = useState("idle");
  const [magicError, setMagicError] = useState("");

  const location = useLocation();

  const initialUser = auth.currentUser;
  const [dest, setDest] = useState(null);
  const [resolving, setResolving] = useState(!!(initialUser && !initialUser.isAnonymous));

  useEffect(() => {
    const from = _.get(location.state, "from");
    const unsub = auth.onAuthStateChanged((user) => {
      if (!user || user.isAnonymous) {
        setResolving(false);
        setDest(null);
        return;
      }
      setResolving(true);
      if (from) {
        setDest(from);
        return;
      }
      resolveHomePath(user.uid).then(setDest);
    });
    return unsub;
  }, [location.state]);

  async function onSubmit(event) {
    event.preventDefault();
    setError("");

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Na succes zorgt de onAuthStateChanged-listener voor de "Doorsturen..."
      // status en de juiste bestemming.
    } catch (err) {
      console.log(err);
      setError("Oeps! Deze gegevens zijn onjuist.");
    }
  }

  async function onRequestMagicLink(event) {
    event.preventDefault();
    if (!magicEmail.trim()) return;

    setMagicStatus("loading");
    setMagicError("");

    try {
      const encodedEmail = btoa(magicEmail).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

      await sendSignInLinkToEmail(auth, magicEmail, {
        url: window.location.origin + "/action?loginEmail=" + encodedEmail + nextParam(_.get(location.state, "from")),
        handleCodeInApp: true,
      });

      setMagicStatus("success");
    } catch (err) {
      console.log(err);
      setMagicStatus("error");
      if (err.code === "auth/invalid-email") {
        setMagicError("Ongeldig emailadres.");
      } else {
        setMagicError("Er ging iets mis. Probeer het opnieuw.");
      }
    }
  }

  if (dest) {
    return <Navigate replace to={dest} />;
  }

  if (resolving) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-secondary">Doorsturen...</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form id="login" onSubmit={onSubmit}>
        <h2 className="text-2xl font-heading font-bold mb-2">Welkom terug</h2>
        <p className="text-sm text-text-secondary mb-6">Log in om je account te beheren.</p>

        {!!error && <Message text={error} />}

        <div className="space-y-4 mb-6">
          <div>
            <label className="label-base">Email</label>
            <input
              name="email"
              type="email"
              autoComplete="email"
              placeholder="Emailadres"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-base"
            />
          </div>
          <div>
            <label className="label-base">Wachtwoord</label>
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder="Wachtwoord"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-base"
            />
          </div>
          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-accent hover:underline underline-offset-2">
              Wachtwoord vergeten?
            </Link>
          </div>
        </div>
        <Button type="submit" label="Inloggen" className="w-full" />
      </form>

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-text-tertiary uppercase tracking-wider">of</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={onRequestMagicLink}>
        <h3 className="text-base font-heading font-semibold mb-3">Inloggen zonder wachtwoord</h3>
        {magicStatus === "success" ? (
          <Message
            variant="success"
            text="Als er een account bestaat met dit e-mailadres, ontvang je binnen enkele minuten een inloglink. Check je inbox!"
          />
        ) : (
          <>
            {magicStatus === "error" && <Message text={magicError} />}
            <div className="space-y-4">
              <input
                name="magic-email"
                type="email"
                autoComplete="email"
                placeholder="Emailadres"
                value={magicEmail}
                onChange={(e) => setMagicEmail(e.target.value)}
                className="input-base"
              />
              <Button
                type="submit"
                variant="secondary"
                label={magicStatus === "loading" ? "Versturen..." : "Stuur inloglink"}
                disabled={magicStatus === "loading"}
                className="w-full"
              />
            </div>
          </>
        )}
      </form>

      <div className="mt-8 p-5 bg-bg-sunken rounded-sm">
        <p className="text-sm font-medium text-center mb-4">Nieuw bij Feestjegeven?</p>
        <div className="space-y-3">
          <Link
            to="/signup"
            className="flex items-center justify-between gap-3 p-3 rounded-sm bg-bg-surface border border-border hover:border-accent transition-all duration-fast"
          >
            <span>
              <span className="block text-sm font-medium">Ik heb een feestlocatie</span>
              <span className="block text-xs text-text-secondary">Meld je feestlocatie aan</span>
            </span>
            <ArrowRight size={16} className="text-accent shrink-0" />
          </Link>
          <Link
            to="/signup-service"
            className="flex items-center justify-between gap-3 p-3 rounded-sm bg-bg-surface border border-border hover:border-accent transition-all duration-fast"
          >
            <span>
              <span className="block text-sm font-medium">Ik bied een dienst aan</span>
              <span className="block text-xs text-text-secondary">Meld je aan als dienstverlener</span>
            </span>
            <ArrowRight size={16} className="text-accent shrink-0" />
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}

export default Login;
